// Vista API Service for Claims Document Processing

import { getAllClaimFiles, clearAllClaimFiles, getClaimFile } from "../utils/claimFilesStorage";

const VISTA_BASE_URL = process.env.REACT_APP_VISTA_BASE_URL;
const VISTA_API_TOKEN = process.env.REACT_APP_VISTA_API_TOKEN;

// With the go-live reverse-proxy setup (VISTA_BASE_URL = same-origin
// /vista-api/...), the edge injects the Authorization header server-side and
// the token is dropped from the bundle — so only send the header when a
// token is actually configured.
const vistaAuthHeaders = () =>
  VISTA_API_TOKEN ? { Authorization: `Bearer ${VISTA_API_TOKEN}` } : {};
const MOTOR_CLAIM_CASE_TYPE_ID = process.env.REACT_APP_VISTA_MOTOR_CLAIM_CASE_TYPE_ID;
// The files-api link route Vista accepts in documentUrls.
const FILES_API_LINK_PREFIX = "https://files-api.solidarity.bh/links/get/";
const MOTOR_CLAIM_DEPARTMENT_ID = process.env.REACT_APP_VISTA_MOTOR_CLAIM_DEPARTMENT_ID;

// The wizard's other Vista routes sit beside the external-cases collection
// VISTA_BASE_URL points at (…/api/external/cases →
// …/api/external/portal/remove-document,
// …/api/external/case-types/:id/documents). Derived rather than configured
// separately so that whatever proxies the validate calls proxies these too —
// which is what keeps the API key server-side at go-live.
const VISTA_EXTERNAL_BASE = (VISTA_BASE_URL || "").replace(/\/cases\/?$/, "");
const VISTA_REMOVE_DOCUMENT_URL = `${VISTA_EXTERNAL_BASE}/portal/remove-document`;
const caseTypeDocumentsUrl = (caseTypeId) =>
  `${VISTA_EXTERNAL_BASE}/case-types/${encodeURIComponent(caseTypeId)}/documents`;

// Firefox asks the OS what a file is and takes "no idea" for an answer, so a
// .heic straight off an iPhone — or anything else the OS has no handler for —
// arrives with an empty `type`. Multipart then labels that part
// application/octet-stream, which Vista's upload filter rejects, and the
// document silently never gets read. Chrome names the same file from its own
// built-in table, which is why this only ever showed up in Firefox. Name it
// ourselves when the browser will not.
const MIME_BY_EXTENSION = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  tif: "image/tiff",
  tiff: "image/tiff",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  pdf: "application/pdf",
};

const withKnownType = (file) => {
  if (!file || file.type) return file;
  if (typeof File === "undefined" || typeof Blob === "undefined") return file;
  if (!(file instanceof Blob)) return file;
  const extension = (file.name || "").split(".").pop().toLowerCase();
  const type = MIME_BY_EXTENSION[extension];
  if (!type) return file;
  try {
    return new File([file], file.name, {
      type,
      lastModified: file.lastModified,
    });
  } catch (error) {
    // A browser that will not re-wrap it still sends the bytes; only the
    // part's content type is lost, which is where this started.
    return file;
  }
};

/**
 * The document slots a case type is configured with
 * (GET /api/external/case-types/:id/documents).
 *
 * One entry per slot — { documentType, displayOrder, isRequired, maxUploads,
 * acceptedFormats } — and it is Vista's admin, not this bundle, that decides
 * how many files a slot takes. That is the point of reading it: claims raised
 * the GDT Accident Report from one file to five pages in Vista's config, with
 * no frontend deploy, and the wizard has to follow the next change too.
 *
 * Fail-open like every other Vista call: null on any failure, and the upload
 * pages fall back to their built-in ceilings rather than refusing to render.
 *
 * @param {Object} [params]
 * @param {string} [params.caseTypeId]
 * @returns {Promise<Object|null>} slots keyed by documentType —
 *   { maxUploads, isRequired, displayOrder, acceptedFormats } — or null
 */
export const fetchCaseTypeDocuments = async ({
  caseTypeId = MOTOR_CLAIM_CASE_TYPE_ID,
} = {}) => {
  if (!caseTypeId) return null;
  try {
    const response = await fetch(caseTypeDocumentsUrl(caseTypeId), {
      method: "GET",
      headers: vistaAuthHeaders(),
    });

    if (!response.ok) {
      console.error("[Vista] case-type documents failed:", response.status);
      return null;
    }

    const result = await response.json();
    // Vista wraps most portal answers in { output: { data } }, and this one is
    // a bare list of slots. Accept either rather than pinning the wizard's
    // upload limits to one envelope shape.
    const slots = [
      result?.output?.data?.documents,
      result?.output?.data,
      result?.data?.documents,
      result?.data,
      result?.documents,
      result,
    ].find(Array.isArray);
    if (!slots) {
      console.error("[Vista] case-type documents: unrecognised response shape");
      return null;
    }

    const byDocumentType = {};
    slots.forEach((slot) => {
      const documentType = slot?.documentType;
      if (!documentType) return;
      byDocumentType[documentType] = {
        maxUploads: Number.isFinite(slot.maxUploads) ? slot.maxUploads : null,
        isRequired: slot.isRequired === true,
        displayOrder: Number.isFinite(slot.displayOrder)
          ? slot.displayOrder
          : null,
        acceptedFormats: Array.isArray(slot.acceptedFormats)
          ? slot.acceptedFormats
          : null,
      };
    });

    console.log(
      "[Vista] case-type document slots:",
      Object.entries(byDocumentType)
        .map(([type, slot]) => `${type}=${slot.maxUploads ?? "?"}`)
        .join(", ")
    );
    return byDocumentType;
  } catch (error) {
    console.error("[Vista] case-type documents error:", error);
    return null;
  }
};

/**
 * Validate a single claim document with Vista (mode=validate-only).
 * Advisory only — nothing is persisted to a case. Sending sessionRef opts the
 * request into Vista's portal gates (rate limit, malware scan, cross-document
 * checks against everything already validated this session).
 *
 * @param {Object} params
 * @param {File|Blob} params.file - The uploaded file
 * @param {string} params.documentType - Vista slot label (e.g. "Driving License 1")
 * @param {string} params.sessionRef - Wizard-session UUID
 * @param {string} [params.policyNumber] - Customer's real policy number if known
 * @param {string} [params.driverCpr] - Driver CPR if collected by then
 * @param {string} [params.caseTypeId]
 * @returns {Promise<Object|null>}
 *   { ok: true, docValid, wrongDocument, data, crossValidation, sessionFiles }
 *     on success:
 *     docValid is output.status — on a single-file call, false means Vista
 *     could not read this document (wrong type, nothing extractable, or
 *     processing error) and the user should re-capture it;
 *     wrongDocument is true when output.data.documentValidation (additive,
 *     ships with Vista's next deploy) says the file failed the document-type
 *     check — i.e. it's the wrong document, not just a bad capture;
 *     sessionFiles is the session's whole inventory after this call, one
 *     { documentType, fileId } per file it holds, which is how a document is
 *     addressed for removal (see removeSessionDocument),
 *   { ok: false, scanFailed: true, documentType } on 422,
 *   { ok: false, rateLimited: true, retryAfterSeconds } on 429,
 *   null on any other error — callers must never block the user on null.
 */
export const validateClaimDocument = async ({
  file,
  documentType,
  sessionRef,
  policyNumber,
  driverCpr,
  caseTypeId = MOTOR_CLAIM_CASE_TYPE_ID,
}) => {
  const formData = new FormData();
  formData.append("mode", "validate-only");
  formData.append("sessionRef", sessionRef);
  if (policyNumber) formData.append("policyNumber", policyNumber);
  if (driverCpr) formData.append("driverCpr", driverCpr);
  formData.append("caseTypeId", caseTypeId);
  formData.append("documentTypes", JSON.stringify([documentType]));
  formData.append("documents", withKnownType(file));

  try {
    const response = await fetch(`${VISTA_BASE_URL}`, {
      method: "POST",
      headers: vistaAuthHeaders(),
      body: formData,
    });

    if (response.status === 422) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        scanFailed: true,
        documentType: body?.documentType || documentType,
      };
    }

    if (response.status === 429) {
      const body = await response.json().catch(() => ({}));
      const headerRetry = Number(response.headers.get("Retry-After"));
      const retryAfterSeconds =
        body?.retryAfterSeconds ||
        (Number.isFinite(headerRetry) ? headerRetry : 60);
      // Loudly. A 429 arms a shared backoff window in the upload pages, and
      // while that window is open NOTHING is sent — so a silent 429 reads
      // from the network tab as the portal having stopped calling Vista at
      // all. The portal's quota is 12/min and 200/day per policy, and a day's
      // testing reaches the second one; when it does, this is the only line
      // that says why.
      console.warn(
        `[Vista] rate limited (429) — no document will be validated for the next ${retryAfterSeconds}s`,
        body?.message || ""
      );
      return { ok: false, rateLimited: true, retryAfterSeconds };
    }

    if (!response.ok) {
      console.error("[Vista] validate-only failed:", response.status);
      return null;
    }

    const result = await response.json();
    const data = result?.output?.data || {};
    const crossValidation = data?.crossValidation || null;
    if (crossValidation) {
      console.log(
        "[Vista] crossValidation:",
        crossValidation.overallStatus,
        (crossValidation.checks || [])
          .map((check) => `${check.checkName}=${check.status}`)
          .join(", ")
      );
    }
    // documentValidation is additive (Vista feature/portal-claim-round2);
    // absent until that deploy — wrongDocument then stays false and callers
    // fall back to the coarser docValid signal.
    const documentValidation = Array.isArray(data?.documentValidation)
      ? data.documentValidation
      : null;
    return {
      ok: true,
      docValid: result?.output?.status !== false,
      wrongDocument:
        documentValidation?.some((doc) => doc?.isValid === false) || false,
      data,
      crossValidation,
      // Per-file handles for the session's documents, in upload order within
      // each slot. Session-scoped and short-lived — they do not survive a
      // Vista restart — so they are only ever held for the wizard's lifetime.
      sessionFiles: Array.isArray(data?.sessionFiles) ? data.sessionFiles : null,
    };
  } catch (error) {
    console.error("[Vista] validate-only error:", error);
    return null;
  }
};

/**
 * Remove one document from the wizard's in-flight Vista session
 * (POST /api/external/portal/remove-document).
 *
 * Mid-wizard there is no case and no document rows to point at — validate-only
 * persists nothing — so the session's files are addressed by documentType plus
 * the per-file handle Vista returns in `sessionFiles`. Without this call a file
 * the customer removes stays in the session, and the cross-document checks go
 * on demanding a plate photo for a vehicle that is no longer in the claim.
 *
 * Vista re-runs those checks over what is left and returns them, so the caller
 * clears a finding about the removed document immediately rather than waiting
 * for the next upload to re-compute it.
 *
 * Fail-open like every other validate-time call: on failure the caller keeps
 * what it did locally, and the mode=full submit re-runs every check against the
 * documents that actually survived.
 *
 * @param {Object} params
 * @param {string} params.sessionRef - The wizard's session UUID
 * @param {string} params.documentType - Vista slot label ("Accident Photos")
 * @param {string} [params.fileId] - One file of a multi-file slot. Omitted, the
 *   whole slot goes — which is what a single-file slot always means.
 * @param {string} [params.caseTypeId] - Sent so the claim type's configured
 *   validation rules are applied when the checks are re-run
 * @returns {Promise<Object|null>}
 *   { ok: true, sessionFound, removed, sessionFiles, crossValidation }, where
 *     sessionFound false means the session expired (nothing left to remove,
 *     no crossValidation) and removed 0 means nothing matched — both are
 *     "already gone", not errors;
 *   null on any failure.
 */
export const removeSessionDocument = async ({
  sessionRef,
  documentType,
  fileId,
  caseTypeId = MOTOR_CLAIM_CASE_TYPE_ID,
}) => {
  if (!sessionRef || !documentType) return null;
  try {
    const response = await fetch(VISTA_REMOVE_DOCUMENT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...vistaAuthHeaders() },
      body: JSON.stringify({
        sessionRef,
        documentType,
        ...(fileId ? { fileId } : {}),
        ...(caseTypeId ? { caseTypeId } : {}),
      }),
    });

    if (!response.ok) {
      console.error(
        "[Vista] remove-document failed:",
        response.status,
        documentType,
        fileId || "(whole slot)"
      );
      return null;
    }

    const result = await response.json();
    const data = result?.output?.data || {};
    console.log(
      `[Vista] removed ${data.removed || 0} file(s) of "${documentType}"`,
      data.sessionFound === false ? "(session expired)" : ""
    );
    return {
      ok: true,
      sessionFound: data.sessionFound !== false,
      removed: data.removed || 0,
      sessionFiles: Array.isArray(data.sessionFiles) ? data.sessionFiles : null,
      crossValidation: data.crossValidation || null,
    };
  } catch (error) {
    console.error("[Vista] remove-document error:", error);
    return null;
  }
};

/**
 * Submit claim documents to Vista API (mode=full — creates the case, async processing)
 * @param {Object} params - The parameters for the API call
 * @param {string} params.customerName - Name of the customer
 * @param {string} params.policyNumber - Customer's real policy number
 * @param {string} [params.crmCaseNumber] - CRM Case Number (e.g., CRM-CLAIM-01089)
 * @param {string[]} params.documentTypes - Array of document type labels
 * @param {File[]} params.documents - Array of File blobs to upload
 * @param {string} [params.crmGuid] - Optional CRM GUID
 * @param {string} [params.sessionRef] - Wizard-session UUID (lets Vista drop the session's PII cache)
 * @param {string} [params.driverCpr] - Driver CPR at time of accident
 * @param {string} [params.accidentDescription] - Accident description from the wizard
 * @param {string} [params.preferredGarage] - Preferred garage
 * @param {string} [params.settlementMethod] - "cash" | "repair"
 * @param {string} [params.whoWasDriving] - Who was driving at the time of accident
 * @param {string} [params.crmApiBase] - The WebAPI the CRM claim was created on; Vista writes its report link there
 * @param {string} [params.caseTypeId] - Optional case type ID (defaults to motor claim)
 * @param {string} [params.departmentId] - Optional department ID (defaults to motor claim department)
 * @returns {Promise<Object>} - API response
 */
export const submitClaimToVista = async ({
  customerName,
  policyNumber,
  crmCaseNumber,
  documentTypes,
  documents,
  crmGuid,
  sessionRef,
  driverCpr,
  accidentDescription,
  preferredGarage,
  settlementMethod,
  whoWasDriving,
  crmApiBase,
  caseTypeId = MOTOR_CLAIM_CASE_TYPE_ID,
  departmentId = MOTOR_CLAIM_DEPARTMENT_ID,
}) => {
  const formData = new FormData();

  formData.append("mode", "full");
  formData.append("customerName", customerName);
  formData.append("policyNumber", policyNumber);
  formData.append("caseTypeId", caseTypeId);
  formData.append("departmentId", departmentId);
  if (crmGuid) {
    formData.append("crmGuid", crmGuid);
  }
  if (crmCaseNumber) formData.append("crmCaseNumber", crmCaseNumber);
  if (sessionRef) formData.append("sessionRef", sessionRef);
  if (driverCpr) formData.append("driverCpr", driverCpr);
  if (accidentDescription)
    formData.append("accidentDescription", accidentDescription);
  if (preferredGarage) formData.append("preferredGarage", preferredGarage);
  if (settlementMethod) formData.append("settlementMethod", settlementMethod);
  if (whoWasDriving) formData.append("whoWasDriving", whoWasDriving);
  // Vista writes the report link on the CRM claim itself (updateclaimreport),
  // so it no longer depends on this page staying open.
  if (crmApiBase) formData.append("crmApiBase", crmApiBase);
  formData.append("documentTypes", JSON.stringify(documentTypes));

  // Every file is already on files-api (uploaded in the wizard, its link in
  // .preview), so Vista downloads them itself: the submit is a few KB instead
  // of re-uploading up to 16 files from the phone. Only if a file has no
  // files-api link are the files uploaded, as before.
  const documentLinks = documents.map((file) => ({
    url: file?.preview,
    name: file?.name,
  }));
  const allLinked =
    documentLinks.length > 0 &&
    documentLinks.every(
      (link) =>
        typeof link.url === "string" && link.url.startsWith(FILES_API_LINK_PREFIX)
    );
  if (allLinked) {
    formData.append("documentUrls", JSON.stringify(documentLinks));
  } else {
    documents.forEach((file) => {
      if (file instanceof File || file instanceof Blob) {
        formData.append("documents", withKnownType(file));
      }
    });
  }

  // One transparent retry on transient failures (network error, 5xx, 429).
  // The caller swallows Vista errors by design (the CRM case already
  // exists), so a failed submit means no Vista report for the adjusters —
  // worth a second attempt before giving up.
  const MAX_ATTEMPTS = 2;
  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(`${VISTA_BASE_URL}`, {
        method: "POST",
        headers: vistaAuthHeaders(),
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        console.log("[Vista] Claim submitted successfully:", result);
        return result;
      }

      const errorText = await response.text();
      lastError = new Error(
        `Vista API error: ${response.status} - ${errorText}`
      );
      // For the caller's failure telemetry: the HTTP status and Vista's
      // error code ({ error: "validation_error", … }), never the message.
      lastError.status = response.status;
      try {
        lastError.vistaError = JSON.parse(errorText)?.error || null;
      } catch (parseError) {
        lastError.vistaError = null;
      }

      const retryable = response.status === 429 || response.status >= 500;
      if (!retryable || attempt === MAX_ATTEMPTS) throw lastError;

      let waitMs = 2000;
      if (response.status === 429) {
        const headerRetry = Number(response.headers.get("Retry-After"));
        if (Number.isFinite(headerRetry)) {
          waitMs = Math.min(headerRetry, 15) * 1000;
        }
      }
      console.warn(
        `[Vista] Submit attempt ${attempt} failed (${response.status}), retrying in ${waitMs}ms`
      );
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    } catch (error) {
      lastError = error;
      const isNetworkError = error instanceof TypeError;
      if (!isNetworkError || attempt === MAX_ATTEMPTS) {
        console.error("[Vista] Error submitting claim:", error);
        throw error;
      }
      console.warn(
        `[Vista] Submit attempt ${attempt} network error, retrying in 2000ms`
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
  throw lastError;
};

/**
 * Build document types array dynamically based on actual uploaded files
 * Each document file needs a corresponding document type label
 *
 * @param {Object} uploadedFiles - Object containing arrays of uploaded files by category
 * @param {string} selectedInsuredType - Type of insured (VEHICLE_DAMAGE, PROPERTY_DAMAGE, BODILY_INJURY)
 * @param {string} selectedCustomer - Customer type (SOLIDARITY_CUSTOMER, THIRD_PARTY_CUSTOMER)
 * @returns {string[]} - Array of document type labels matching the order of documents
 */
export const buildDocumentTypesArray = (uploadedFiles, selectedInsuredType, selectedCustomer) => {
  const documentTypes = [];

  if (selectedCustomer === "SOLIDARITY_CUSTOMER") {
    // Solidarity Customer - Motor Claim
    // Driving License 1 & 2
    if (uploadedFiles.driverDocs?.[0]) documentTypes.push("Driving License 1");
    if (uploadedFiles.driverDocs?.[1]) documentTypes.push("Driving License 2");

    // Vehicle Ownership 1 & 2
    if (uploadedFiles.ownerShipDocs?.[0]) documentTypes.push("Vehicle Ownership 1");
    if (uploadedFiles.ownerShipDocs?.[1]) documentTypes.push("Vehicle Ownership 2");

    // Accident Photos (1-10, each gets same label)
    uploadedFiles.accidentDocs?.forEach(() => documentTypes.push("Accident Photos"));

    // GDT Accident Report (can have multiple pages)
    uploadedFiles.incidentDoc?.forEach(() => documentTypes.push("GDT Accident Report"));

    return documentTypes;
  }

  switch (selectedInsuredType) {
    case "VEHICLE_DAMAGE":
      // Third Party - Vehicle Damage
      // Driving License 1 & 2
      if (uploadedFiles.driverDocs?.[0]) documentTypes.push("Driving License 1");
      if (uploadedFiles.driverDocs?.[1]) documentTypes.push("Driving License 2");

      // Insurance Certificate
      uploadedFiles.insuranceCert?.forEach(() => documentTypes.push("Insurance Certificate"));

      // Vehicle Ownership 1 & 2
      if (uploadedFiles.ownerShipDocs?.[0]) documentTypes.push("Vehicle Ownership 1");
      if (uploadedFiles.ownerShipDocs?.[1]) documentTypes.push("Vehicle Ownership 2");

      // Accident Photos (1-10)
      uploadedFiles.accidentDocs?.forEach(() => documentTypes.push("Accident Photos"));

      // GDT Accident Report
      uploadedFiles.incidentDoc?.forEach(() => documentTypes.push("GDT Accident Report"));
      break;

    case "PROPERTY_DAMAGE":
      // Third Party - Property Damage
      // Property Owner CPR
      uploadedFiles.propertyOwner?.forEach(() => documentTypes.push("Property Owner CPR"));

      // Proof of Property Ownership
      uploadedFiles.proofAddress?.forEach(() => documentTypes.push("Proof of Property Ownership"));

      // Accident Photos
      uploadedFiles.propertyAccidentPhotosDocs?.forEach(() => documentTypes.push("Accident Photos"));

      // GDT Accident Report
      uploadedFiles.incidentDoc?.forEach(() => documentTypes.push("GDT Accident Report"));
      break;

    case "BODILY_INJURY":
      // Third Party - Bodily Injury
      // Impacted Person CPR
      uploadedFiles.impactedPersonCprDocs?.forEach(() => documentTypes.push("Impacted Person CPR"));

      // Judicial Report
      uploadedFiles.judicialReportDocs?.forEach(() => documentTypes.push("Judicial Report"));

      // Medical Report (otherDocs in this flow)
      uploadedFiles.otherDocs?.forEach(() => documentTypes.push("Medical Report"));

      // GDT Accident Report
      uploadedFiles.incidentDoc?.forEach(() => documentTypes.push("GDT Accident Report"));
      break;

    default:
      // Fallback
      uploadedFiles.incidentDoc?.forEach(() => documentTypes.push("GDT Accident Report"));
  }

  return documentTypes;
};

/**
 * Collect document files in the correct order matching buildDocumentTypesArray
 * @param {Object} uploadedFiles - Object containing arrays of uploaded files by category
 * @param {string} selectedInsuredType - Type of insured
 * @param {string} selectedCustomer - Customer type
 * @returns {File[]} - Array of File blobs in order matching documentTypes
 */
export const collectDocumentsInOrder = (uploadedFiles, selectedInsuredType, selectedCustomer) => {
  const files = [];

  // Helper to get File blob from uploaded file object using its _key
  const getFileBlob = (uploadedFile) => {
    if (!uploadedFile?._key) return null;
    return getClaimFile(uploadedFile._key);
  };

  // Helper to add files from an array
  const addFilesFromArray = (arr) => {
    arr?.forEach(file => {
      const blob = getFileBlob(file);
      if (blob) files.push(blob);
    });
  };

  if (selectedCustomer === "SOLIDARITY_CUSTOMER") {
    // Same order as buildDocumentTypesArray for SOLIDARITY_CUSTOMER
    if (uploadedFiles.driverDocs?.[0]) {
      const blob = getFileBlob(uploadedFiles.driverDocs[0]);
      if (blob) files.push(blob);
    }
    if (uploadedFiles.driverDocs?.[1]) {
      const blob = getFileBlob(uploadedFiles.driverDocs[1]);
      if (blob) files.push(blob);
    }
    if (uploadedFiles.ownerShipDocs?.[0]) {
      const blob = getFileBlob(uploadedFiles.ownerShipDocs[0]);
      if (blob) files.push(blob);
    }
    if (uploadedFiles.ownerShipDocs?.[1]) {
      const blob = getFileBlob(uploadedFiles.ownerShipDocs[1]);
      if (blob) files.push(blob);
    }
    addFilesFromArray(uploadedFiles.accidentDocs);
    addFilesFromArray(uploadedFiles.incidentDoc);
    return files;
  }

  switch (selectedInsuredType) {
    case "VEHICLE_DAMAGE":
      if (uploadedFiles.driverDocs?.[0]) {
        const blob = getFileBlob(uploadedFiles.driverDocs[0]);
        if (blob) files.push(blob);
      }
      if (uploadedFiles.driverDocs?.[1]) {
        const blob = getFileBlob(uploadedFiles.driverDocs[1]);
        if (blob) files.push(blob);
      }
      addFilesFromArray(uploadedFiles.insuranceCert);
      if (uploadedFiles.ownerShipDocs?.[0]) {
        const blob = getFileBlob(uploadedFiles.ownerShipDocs[0]);
        if (blob) files.push(blob);
      }
      if (uploadedFiles.ownerShipDocs?.[1]) {
        const blob = getFileBlob(uploadedFiles.ownerShipDocs[1]);
        if (blob) files.push(blob);
      }
      addFilesFromArray(uploadedFiles.accidentDocs);
      addFilesFromArray(uploadedFiles.incidentDoc);
      break;

    case "PROPERTY_DAMAGE":
      addFilesFromArray(uploadedFiles.propertyOwner);
      addFilesFromArray(uploadedFiles.proofAddress);
      addFilesFromArray(uploadedFiles.propertyAccidentPhotosDocs);
      addFilesFromArray(uploadedFiles.incidentDoc);
      break;

    case "BODILY_INJURY":
      addFilesFromArray(uploadedFiles.impactedPersonCprDocs);
      addFilesFromArray(uploadedFiles.judicialReportDocs);
      addFilesFromArray(uploadedFiles.otherDocs);
      addFilesFromArray(uploadedFiles.incidentDoc);
      break;
  }

  return files;
};

/**
 * Collect all document files from the in-memory storage
 * File blobs cannot be stored in Redux, so we use a separate storage
 * @returns {File[]} - Array of File blobs
 */
export const collectDocumentFiles = () => {
  const files = getAllClaimFiles();
  console.log("[Vista] Collected files from storage:", files.length);
  return files;
};

/**
 * Clear all claim files from storage
 * Call this after successful submission or when user exits the flow
 */
export { clearAllClaimFiles } from "../utils/claimFilesStorage";
